import React, { useState, useEffect } from 'react';
import { useSocketContext } from '../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  data: any;
  read: boolean;
  createdAt: string;
}

const PushNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const { socket } = useSocketContext();
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();

    if (socket) {
      const handlePushNotification = (data: Notification) => {
        // Add notification to list
        setNotifications(prev => [data, ...prev]);
        
        // Show browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          showBrowserNotification(data);
        }
      };

      socket.on('push-notification', handlePushNotification);

      return () => {
        socket.off('push-notification', handlePushNotification);
      };
    }
  }, [socket]);

  useEffect(() => {
    // Request notification permission on mount
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/notifications/unread`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const showBrowserNotification = (notification: Notification) => {
    const browserNotification = new Notification(notification.title, {
      body: notification.message,
      icon: '/favicon.ico', // You might want to add a proper icon
      badge: '/favicon.ico',
      tag: notification.id.toString(),
      requireInteraction: false
    });

    browserNotification.onclick = () => {
      window.focus();
      handleNotificationClick(notification);
      browserNotification.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => {
      browserNotification.close();
    }, 5000);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    try {
      await fetch(`${API_URL}/notifications/read/${notification.id}`, {
        method: 'POST',
        credentials: 'include'
      });
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }

    // Navigate based on notification type
    if (notification.type === 'game_invitation') {
      // Invitation is already handled by InvitationContext
      // Just close the notification panel
      setShowNotifications(false);
    } else if (notification.type === 'friend_request') {
      navigate('/friends?tab=requests');
      setShowNotifications(false);
    } else if (notification.type === 'friend_request_accepted') {
      navigate('/friends');
      setShowNotifications(false);
    } else if (notification.type === 'chat_message') {
      // Don't navigate for chat messages, user can open chat themselves
      setShowNotifications(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/notifications/read-all`, {
        method: 'POST',
        credentials: 'include'
      });
      setNotifications([]);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      {/* Notification bell - always visible */}
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className={`fixed bottom-4 left-4 z-40 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-full shadow-lg transition duration-200 flex items-center gap-2 min-h-[44px] ${
          showNotifications ? 'bg-blue-700' : ''
        }`}
        style={{ zIndex: 40 }}
        aria-label={showNotifications ? 'Benachrichtigungen schließen' : 'Benachrichtigungen öffnen'}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications panel */}
      {showNotifications && (
        <div className="fixed bottom-20 left-4 z-40 w-80 sm:w-96 bg-white rounded-lg shadow-2xl border border-gray-300 flex flex-col max-h-[500px] min-h-[300px]">
          {/* Header */}
          <div className="bg-blue-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
            <h3 className="font-semibold text-lg">Benachrichtigungen</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-white hover:text-gray-200 transition text-xs"
                >
                  Alle gelesen
                </button>
              )}
              <button
                onClick={() => setShowNotifications(false)}
                className="text-white hover:text-gray-200 transition"
                aria-label="Schließen"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Notifications list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
            {notifications.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Keine Benachrichtigungen
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-pointer hover:bg-gray-50 transition ${
                    !notification.read ? 'border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-800 mb-1">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-600 mb-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(notification.createdAt).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PushNotifications;

